import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useApp } from '../lib/app-context';
import { GENDER_LABEL, addDays, mad, today } from '../lib/format';
import { cityOptions, useCities } from '../lib/places';
import { Field, Notice, Select, Spinner, TextInput, Toggle } from '../components/ui';
import type { ColocationRequest } from '../lib/types';
import { t } from '../lib/i18n';

/** Post or edit a colocation request, with a live preview of how it will look. */
export default function ColocationForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, toast, reportError } = useApp();
  const editing = Boolean(id);

  const [loading, setLoading] = useState(editing);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [city, setCity] = useState(user?.city ?? 'Rabat');
  const [neighborhood, setNeighborhood] = useState('');
  const [budgetMin, setBudgetMin] = useState('1400');
  const [budgetMax, setBudgetMax] = useState('1900');
  const [moveInDate, setMoveInDate] = useState(addDays(today(), 30));
  const [durationMonths, setDurationMonths] = useState(10);
  const [flatmatesWanted, setFlatmatesWanted] = useState(1);
  const [genderPreference, setGenderPreference] = useState('ANY');
  const [nonSmoker, setNonSmoker] = useState(true);
  const [bio, setBio] = useState('');
  const [universityName, setUniversityName] = useState(user?.universityName ?? '');
  const [isPublic, setIsPublic] = useState(true);
  const [status, setStatus] = useState('ACTIVE');
  const cities = useCities();

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const response = await api.get<{ request: ColocationRequest }>(`/colocation-requests/${id}`);
        const request = response.request;
        setCity(request.city);
        setNeighborhood(request.neighborhood ?? '');
        setBudgetMin(String(request.budget.minCentimes / 100));
        setBudgetMax(String(request.budget.maxCentimes / 100));
        setMoveInDate(request.moveInDate);
        setDurationMonths(request.durationMonths);
        setFlatmatesWanted(request.flatmatesWanted);
        setGenderPreference(request.genderPreference);
        setNonSmoker(request.smokingPreference === 'NON_SMOKER');
        setBio(request.bio);
        setUniversityName(request.universityName ?? '');
        setIsPublic(request.visibility === 'PUBLIC');
        setStatus(request.status);
      } catch (error) {
        reportError(error, t("Demande introuvable"));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, reportError]);

  const submit = async () => {
    setBusy(true);
    setErrors({});
    const payload = {
      city,
      ...(neighborhood ? { neighborhood } : {}),
      budgetMinCentimes: Number(budgetMin || 0) * 100,
      budgetMaxCentimes: Number(budgetMax || 0) * 100,
      moveInDate,
      durationMonths,
      flatmatesWanted,
      genderPreference,
      smokingPreference: nonSmoker ? 'NON_SMOKER' : 'SMOKER_OK',
      bio,
      ...(universityName ? { universityName } : {}),
      visibility: isPublic ? 'PUBLIC' : 'PRIVATE',
      ...(editing ? { status } : {}),
    };
    try {
      if (editing) {
        await api.patch(`/colocation-requests/${id}`, payload);
        toast(t("Demande mise à jour."), 'ok');
      } else {
        await api.post('/colocation-requests', payload);
        toast(t("Demande publiée. Les hôtes et colocataires peuvent vous écrire."), 'ok');
      }
      navigate('/colocations');
    } catch (error) {
      const apiError = reportError(error);
      if (apiError instanceof ApiError) setErrors(apiError.fieldErrors());
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/colocation-requests/${id}`);
      toast(t("Demande supprimée."));
      navigate('/compte');
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="page center" style={{ paddingTop: 60 }}>
        <Spinner label={t("Chargement…")} />
      </div>
    );
  }

  return (
    <div className="page page--mid stack stack--loose">
      <h1 style={{ fontSize: 26 }}>{editing ? t("Modifier ma demande") : t("Ma demande de colocation")}</h1>

      <div className="detail-layout">
        <div className="card card--pad stack">
          <div className="row row--wrap" style={{ gap: 10 }}>
            <div style={{ flex: '1 1 160px' }}>
              <Field label={t("Ville")} error={errors.city}>
                <Select
                  value={city}
                  onChange={setCity}
                  options={cityOptions(cities)}
                />
              </Field>
            </div>
            <div style={{ flex: '1 1 160px' }}>
              <Field label={t("Quartier souhaité")}>
                <TextInput value={neighborhood} onChange={setNeighborhood} placeholder={t("Agdal, Hassan…")} />
              </Field>
            </div>
          </div>

          <div className="row row--wrap" style={{ gap: 10 }}>
            <div style={{ flex: '1 1 140px' }}>
              <Field label={t("Budget min (DH/mois)")} error={errors.budgetMinCentimes}>
                <TextInput
                  value={budgetMin}
                  onChange={(value) => setBudgetMin(value.replace(/\D/g, ''))}
                  inputMode="numeric"
                />
              </Field>
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <Field label={t("Budget max (DH/mois)")} error={errors.budgetMaxCentimes}>
                <TextInput
                  value={budgetMax}
                  onChange={(value) => setBudgetMax(value.replace(/\D/g, ''))}
                  inputMode="numeric"
                />
              </Field>
            </div>
          </div>

          <div className="row row--wrap" style={{ gap: 10 }}>
            <Field label={t("Emménagement")} error={errors.moveInDate}>
              <input
                className="input"
                type="date"
                min={today()}
                value={moveInDate}
                onChange={(event) => setMoveInDate(event.target.value)}
              />
            </Field>
            <Field label={t("Durée")}>
              <Select
                value={String(durationMonths)}
                onChange={(value) => setDurationMonths(Number(value))}
                options={[3, 6, 9, 10, 12, 24].map((count) => ({
                  value: String(count),
                  label: t('{count} mois', { count }),
                }))}
              />
            </Field>
            <Field label={t("Colocataires recherchés")}>
              <Select
                value={String(flatmatesWanted)}
                onChange={(value) => setFlatmatesWanted(Number(value))}
                options={[1, 2, 3, 4].map((count) => ({ value: String(count), label: String(count) }))}
              />
            </Field>
          </div>

          <div className="row row--wrap" style={{ gap: 10 }}>
            <div style={{ flex: '1 1 180px' }}>
              <Field label={t("Préférence")}>
                <Select
                  value={genderPreference}
                  onChange={setGenderPreference}
                  options={Object.entries(GENDER_LABEL).map(([value, label]) => ({ value, label }))}
                />
              </Field>
            </div>
            <div style={{ flex: '1 1 180px' }}>
              <Field label={t("Université (optionnel)")}>
                <TextInput value={universityName} onChange={setUniversityName} placeholder={t("UM5, UM6P…")} />
              </Field>
            </div>
          </div>

          <Toggle on={nonSmoker} onChange={setNonSmoker} label={t("Non-fumeur")} />
          <Toggle
            on={isPublic}
            onChange={setIsPublic}
            label={t("Visible dans le fil public")}
          />
          {!isPublic ? (
            <Notice tone="info">
              {t("Une demande privée n’apparaît pas dans le fil : vous seul la voyez.")}
            </Notice>
          ) : null}

          {editing ? (
            <Field label={t("Statut")}>
              <Select
                value={status}
                onChange={setStatus}
                options={[
                  { value: 'ACTIVE', label: t("Active") },
                  { value: 'PAUSED', label: t("En pause") },
                  { value: 'FULFILLED', label: t("Trouvé — clôturée") },
                ]}
              />
            </Field>
          ) : null}

          <Field label={t("Présentez-vous")} error={errors.bio}>
            <textarea
              className="textarea"
              rows={5}
              value={bio}
              onChange={(event) => setBio(event.target.value)}
              placeholder={t("Étudiante en médecine à l’UM5, calme et organisée, non-fumeuse…")}
            />
          </Field>

          <div className="row" style={{ gap: 10 }}>
            <button className="btn" style={{ flex: 1 }} disabled={busy} onClick={submit}>
              {busy ? t("Enregistrement…") : editing ? t("Enregistrer") : t("Publier la demande")}
            </button>
            {editing ? (
              <button className="btn btn--danger" disabled={busy} onClick={remove}>
                {t("Supprimer")}
              </button>
            ) : null}
          </div>
        </div>

        <aside className="sticky-panel">
          <div className="card card--pad stack stack--tight">
            <span className="field__label">{t("Aperçu public")}</span>
            <span className="strong">{user?.fullName}</span>
            <span className="small muted">
              {t(city)}
              {neighborhood ? ` · ${neighborhood}` : ''}
            </span>
            <span className="strong">
              {mad(Number(budgetMin || 0) * 100)} – {mad(Number(budgetMax || 0) * 100)}
              <span className="muted small"> /mois</span>
            </span>
            <span className="small muted">
              {durationMonths} {t("mois ·")} {flatmatesWanted} {t("coloc ·")} {GENDER_LABEL[genderPreference]}
              {nonSmoker ? t(" · non-fumeur") : ''}
            </span>
            {bio ? (
              <p className="small" style={{ margin: '6px 0 0' }}>
                {bio}
              </p>
            ) : null}
            <p className="tiny muted" style={{ margin: '8px 0 0' }}>
              {t("Votre numéro de téléphone n’est jamais affiché. Les échanges passent par la messagerie Bedly.")}
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
